import { useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { ShieldCheck, ShieldX, X, Loader2, Wallet } from 'lucide-react';
import { useAppMode } from '../context/DemoContext';

/**
 * LiveModeAuthModal
 *
 * Cyan terminal-styled modal that gates Live Mode behind an authorized-wallet
 * verification step. Three states:
 *
 *   ─ pending: no wallet connected → "Connect Wallet to Verify" CTA
 *   ─ authorized: connected wallet ∈ authorizedWallets → green badge, auto-promote to Live
 *   ─ denied:     connected wallet ∉ authorizedWallets → red badge, copy explaining devnet is admin-only
 *
 * Open/close is driven by the `open` prop. Caller (HomePage) controls visibility.
 */
const truncate = (s) => (s ? `${s.slice(0, 6)}…${s.slice(-4)}` : '—');

export default function LiveModeAuthModal({ open, onClose }) {
  const { connected, publicKey } = useWallet();
  const { setVisible } = useWalletModal();
  const { connectedWallet, isLiveAuthorized, enterLive, mode, authorizedWallets } = useAppMode();

  // Derived state — re-evaluated each render based on live wallet/context state.
  const state = useMemo(() => {
    if (!connected || !publicKey) return 'pending';
    return isLiveAuthorized ? 'authorized' : 'denied';
  }, [connected, publicKey, isLiveAuthorized]);

  // Auto-promote to live when wallet authorizes while modal is open.
  useEffect(() => {
    if (!open) return;
    if (state === 'authorized' && mode !== 'live') {
      // Tiny delay so user sees the green "AUTHORIZED" tick before transition.
      const id = setTimeout(() => {
        enterLive();
        onClose?.();
      }, 900);
      return () => clearTimeout(id);
    }
  }, [open, state, mode, enterLive, onClose]);

  if (typeof window === 'undefined') return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[120] flex items-center justify-center px-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          data-testid="live-auth-modal"
          onClick={onClose}
        >
          {/* Backdrop */}
          <div className="absolute inset-0" style={{ background: 'rgba(2,4,9,0.86)', backdropFilter: 'blur(8px)' }} />

          <motion.div
            className="relative w-full max-w-md rounded-xl overflow-hidden"
            initial={{ y: 16, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 8, opacity: 0, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 180, damping: 22 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'linear-gradient(180deg, rgba(8,14,24,0.96) 0%, rgba(4,8,16,0.98) 100%)',
              border: '1px solid rgba(125,208,255,0.32)',
              boxShadow: '0 0 80px rgba(125,208,255,0.18), 0 0 24px rgba(125,208,255,0.10) inset',
            }}
          >
            {/* Top scanline accent */}
            <div className="h-[2px]" style={{ background: 'linear-gradient(90deg, transparent, #7dd0ff, transparent)' }} />

            {/* Close */}
            <button
              onClick={onClose}
              data-testid="live-auth-modal-close"
              className="absolute top-3 right-3 p-1 rounded hover:bg-white/5 transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4" style={{ color: 'rgba(168,222,247,0.7)' }} />
            </button>

            <div className="px-7 pt-7 pb-6">
              {/* Header */}
              <div className="flex items-center gap-2 mb-1">
                <span
                  className="inline-block rounded-full"
                  style={{
                    width: 6, height: 6,
                    background: '#7dd0ff',
                    boxShadow: '0 0 8px #7dd0ff',
                    animation: 'pulse 1.6s ease-in-out infinite',
                  }}
                />
                <span
                  className="text-[10px] tracking-[0.32em] font-bold uppercase"
                  style={{ color: '#a8def7', fontFamily: "'JetBrains Mono','Courier New',monospace" }}
                >
                  Devnet Authorization
                </span>
              </div>
              <h2
                className="text-xl font-bold mb-3"
                style={{
                  fontFamily: "'Cinzel', serif",
                  background: 'linear-gradient(90deg, #7dd0ff, #c5e7ff, #a892ff)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  filter: 'drop-shadow(0 0 6px rgba(125,208,255,0.4))',
                }}
              >
                Verify Your Wallet
              </h2>
              <p
                className="text-[13px] leading-relaxed mb-5"
                style={{ fontFamily: "'EB Garamond', serif", color: 'rgba(220,235,250,0.72)' }}
              >
                Live Mode reads and writes to Solana devnet. Access is restricted to{' '}
                <span style={{ color: '#a8def7' }}>{authorizedWallets.length} authorized wallets</span>{' '}
                during pre-launch testing.
              </p>

              {/* Body — depends on state */}
              {state === 'pending' && (
                <div data-testid="live-auth-state-pending">
                  <div
                    className="rounded-lg px-4 py-3 mb-4 flex items-center gap-3"
                    style={{
                      background: 'rgba(125,208,255,0.05)',
                      border: '1px solid rgba(125,208,255,0.18)',
                    }}
                  >
                    <Wallet className="w-4 h-4" style={{ color: '#7dd0ff' }} />
                    <span
                      className="text-[12px] tracking-[0.10em]"
                      style={{ color: 'rgba(220,235,250,0.85)', fontFamily: "'JetBrains Mono','Courier New',monospace" }}
                    >
                      No wallet connected
                    </span>
                  </div>
                  <button
                    onClick={() => setVisible(true)}
                    data-testid="live-auth-connect-btn"
                    className="w-full rounded-lg py-3 font-bold uppercase tracking-[0.18em] text-[12px] transition-all hover:scale-[1.02] active:scale-[0.98]"
                    style={{
                      background: 'linear-gradient(90deg, #7dd0ff, #a892ff)',
                      color: '#04080f',
                      fontFamily: "'Cinzel', serif",
                      boxShadow: '0 0 24px rgba(125,208,255,0.35)',
                    }}
                  >
                    Connect Wallet to Verify
                  </button>
                </div>
              )}

              {state === 'authorized' && (
                <div data-testid="live-auth-state-authorized">
                  <div
                    className="rounded-lg px-4 py-4 mb-4 flex items-center gap-3"
                    style={{
                      background: 'rgba(57,255,20,0.06)',
                      border: '1px solid rgba(57,255,20,0.35)',
                      boxShadow: '0 0 24px rgba(57,255,20,0.12) inset',
                    }}
                  >
                    <ShieldCheck className="w-5 h-5" style={{ color: '#39ff14', filter: 'drop-shadow(0 0 6px #39ff14)' }} />
                    <div className="flex-1">
                      <div
                        className="text-[10px] tracking-[0.32em] font-bold uppercase mb-0.5"
                        style={{ color: '#39ff14', fontFamily: "'JetBrains Mono','Courier New',monospace" }}
                      >
                        Authorized
                      </div>
                      <div
                        className="text-[11px]"
                        style={{ color: 'rgba(220,235,250,0.78)', fontFamily: "'JetBrains Mono','Courier New',monospace" }}
                      >
                        {truncate(connectedWallet)}
                      </div>
                    </div>
                    <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#39ff14' }} />
                  </div>
                  <p
                    className="text-[12px] text-center"
                    style={{ fontFamily: "'EB Garamond', serif", color: 'rgba(220,235,250,0.55)' }}
                  >
                    Entering devnet…
                  </p>
                </div>
              )}

              {state === 'denied' && (
                <div data-testid="live-auth-state-denied">
                  <div
                    className="rounded-lg px-4 py-4 mb-4"
                    style={{
                      background: 'rgba(255,77,77,0.06)',
                      border: '1px solid rgba(255,77,77,0.35)',
                    }}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <ShieldX className="w-5 h-5" style={{ color: '#ff4d4d', filter: 'drop-shadow(0 0 6px rgba(255,77,77,0.5))' }} />
                      <div
                        className="text-[10px] tracking-[0.32em] font-bold uppercase"
                        style={{ color: '#ff4d4d', fontFamily: "'JetBrains Mono','Courier New',monospace" }}
                      >
                        Access Denied
                      </div>
                    </div>
                    <div
                      className="text-[11px] mb-1"
                      style={{ color: 'rgba(220,235,250,0.78)', fontFamily: "'JetBrains Mono','Courier New',monospace" }}
                    >
                      {truncate(connectedWallet)}
                    </div>
                    <p
                      className="text-[12px] mt-2"
                      style={{ fontFamily: "'EB Garamond', serif", color: 'rgba(220,235,250,0.7)' }}
                    >
                      This wallet is not on the devnet allowlist. Live Mode is restricted during pre-launch testing — try Demo Mode for full UX exploration, or connect an authorized wallet.
                    </p>
                  </div>
                  <button
                    onClick={onClose}
                    data-testid="live-auth-deny-close"
                    className="w-full rounded-lg py-3 font-bold uppercase tracking-[0.18em] text-[12px] transition-all hover:bg-white/5"
                    style={{
                      border: '1px solid rgba(168,222,247,0.32)',
                      color: '#a8def7',
                      fontFamily: "'Cinzel', serif",
                    }}
                  >
                    Close
                  </button>
                </div>
              )}
            </div>

            {/* Bottom accent */}
            <div className="h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(125,208,255,0.32), transparent)' }} />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
